import getInitialWorkspaceSidebarCollapsed from './getInitialWorkspaceSidebarCollapsed.js';
import extractRouteThreadId from '@/redux/selectors/pieveSelectors/lib/extractRouteThreadId.js';
import readCurrentPieveRoutePathname from '@/lib/routes/readCurrentPieveRoutePathname.js';

const createInitialState = () => {
  return {
    fixedWidth: false,
    credentialImpactResponse: null,
    credentialImpactLoadRequestId: null,
    isWorkspaceCompanyListLoading: false,
    workspaceCompanyListError: '',
    workspaceSwitchingCompanyId: null,
    workspaceSidebarCollapsed: getInitialWorkspaceSidebarCollapsed(),
    desktopSidebarPeek: 'closed',
    // Which half of a single-column workflow thread is showing. The conversation is the primary view
    // because it is the only way to change the workflow; the workflow itself is read-only browsing.
    workflowSurfaceSingleColumnView: 'chat',
    activeChecklistByThreadId: {},
    olderThreadEventsByThreadId: {},
    // What Pieve Desktop reports about Claude Code on this Mac (`window.pieve.planRuns.readiness`), or null outside it.
    // Forced readiness reads in flight, so every surface that shows Claude's status can show that it is checking.
    // The run choice picked for a thread on this device, keyed by thread id: a Claude model, or `{ lane: 'pieve' }`. The
    // thread's own composerModel holds only Pieve's models, so the plan pick never reaches the server.
    mainThreadTerminalOpenByThreadId: {},
    mainThreadSubagentPaneHiddenByScope: {},
    composerAttachmentsByScope: {},
    composerAttachmentContextBudgetByScope: {},
    composerAttachmentOverflowStrategyByScope: {},
    composerAttachmentSkillSuggestionRequestByScope: {},
    composerPendingInteractionIndexByScope: {},
    voiceRecording: {
      isRecording: false,
      isRecordingStarting: false,
      isTranscribing: false,
      duration: 0,
      signalLevel: 0,
      startRequestId: 0,
      stopRequestId: 0,
      stopSubmitAfter: false,
      requestScopeKey: '',
      scopeKey: '',
      chunks: []
    },
    composerSendRequestId: 0,
    composerSendRequestScopeKey: '',
    composerSubmittedId: 0,
    composerSubmittedScopeKey: '',
    requestLoadersByKey: {},
    websiteSignupSubtitles: [],
    prefs: {},
    prefsPendingMutations: [],
    prefsRequestStatus: 'idle',
    prefsServerPayload: {},
    settings: {
      isLoading: false,
      isSaving: false,
      error: '',
      hasResolvedInitialSettings: false,
      archivedThreadsRefreshKey: 0
    },
    threadContextPreview: null,
    threadRunInteractionResponse: null,
    pendingSkillDraftItem: null,
    mainThreadHydrationSettled: false,
    selectedThreadId: null,
    activeThreadId: extractRouteThreadId({ pathname: readCurrentPieveRoutePathname() }) || null,
    adminThreadViewId: null,
    threadSurfacePresentation: null,
    threadSurfaceSignalQueue: [],
    threadSurfaceSubmitRequest: null,
    threadSurfaceViewport: {
      scrollTop: 0,
      scrollHeight: 0,
      clientHeight: 0,
      distanceToBottom: 0
    },
    threadSurfaceRouteHydration: {
      threadId: null,
      requestId: 0,
      loading: false
    },
    isAutoPendingNewThread: false,
    composerFocusRequestBySurface: {
      main: 0,
      schedule: 0,
      trigger: 0,
      workflow: 0
    },
    crossThreadEmptyStateTransition: null,
    emptyStateSubmission: null,
    primaryActionRequest: {
      requestId: 0,
      actionId: null,
      actionContext: null
    },
    skillDraftThreadStartRequest: {
      requestId: 0,
      skillId: null,
      mode: null
    },
    automations: {
      activePane: 'schedules',
      triggerSelectionId: null,
      scheduleSelectionId: null,
      scheduleRunSelectionContext: {
        scheduleId: null,
        runThreadId: null
      },
      triggerRunSelectionContext: {
        triggerId: null,
        runThreadId: null
      }
    },
    toolTraceInspector: {
      expandedRunId: null,
      popoverLayout: null
    },
    /* The publish-as-template pipeline as the author watches it: which stage is running, what the
       terminal event returned, or why it failed. Request-shaped presentation state, so it lives here
       and not in the analytics slice; the template ROW the pipeline persists is filed there. One at a
       time, because one publish modal is open at a time. */
    artifactSlider: {
      open: false,
      selectedKey: null,
      items: []
    },
    modelPickerHowItWorksOpen: false,
    eventKeys: {},
    modals: {},
    // A MindCloud staff member's access to restricted organizations, by company id (GET_MY_STAFF_ACCESS).
    activeContextMenu: null
  };
};

export default createInitialState;
